<template>
  <div>
    <div class="row">
      <div class="col-md-12 grid-margin">
        <div class="row">
          <div class="col-8 mb-0 mb-xl-0">
            <h4 class="font-weight-bold">Merchant Transactions</h4>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Filters -->
    <div class="card">
      <div class="card-body">
        <form>
          <div class="form-group row">
            <label class="col-sm-2 col-form-label">Date Range</label>
            <div class="col-sm-4">
              <Datepicker
                v-model="dateRange"
                range
                multiCalendars
                autoApply
                style="width: 98%; padding: 8px"
                placeholder="Select Date Range"
                :enableTimePicker="false"
                dark
              ></Datepicker>
            </div>
            <div class="col-sm-2">
              <select class="form-control" v-model="filters.status">
                <option value="">All Status</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
            <div class="col-sm-2">
              <select class="form-control" v-model="filters.transactionType">
                <option value="">All Types</option>
                <option v-for="type in transactionTypes" :key="type" :value="type">
                  {{ type.toUpperCase() }}
                </option>
              </select>
            </div>
            <div class="col-sm-2">
              <button type="button" class="btn btn-primary" @click="searchTransactions" :disabled="isLoading">
                <span v-if="isLoading" class="spinner-border spinner-border-sm mr-1"></span>
                Search
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="card mt-4">
      <div class="card-body">
        <div class="table-responsive">
          <table id="transactions-table" class="table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="transaction in transactions" :key="transaction.reference">
                <td>{{ transaction.reference }}</td>
                <td>{{ transaction.transactionType }}</td>
                <td>{{ transaction.amount }}</td>
                <td>
                  <span :class="getStatusBadgeClass(transaction.status)">
                    {{ transaction.status }}
                  </span>
                </td>
                <td>{{ formatDate(transaction.createdAt) }}</td>
                <td>
                  <button class="btn btn-sm btn-outline-info" @click="viewTransactionDetails(transaction)">
                    <i class="fa fa-eye"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="transactions.length === 0">
                <td colspan="6" class="text-center">No transactions found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Transaction Details Modal -->
    <div class="modal fade" id="transactionDetailsModal" tabindex="-1" role="dialog" aria-labelledby="transactionDetailsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="transactionDetailsModalLabel">Transaction Details</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="closeModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" v-if="selectedTransaction">
            <div class="row">
              <div class="col-md-6">
                <p><strong>Transaction ID:</strong> {{ selectedTransaction.reference }}</p>
                <p><strong>Type:</strong> {{ selectedTransaction.transactionType }}</p>
                <p><strong>Amount:</strong> {{ selectedTransaction.amount }}</p>
                <p><strong>Status:</strong> {{ selectedTransaction.status }}</p>
              </div>
              <div class="col-md-6">
                <p><strong>Date:</strong> {{ formatDate(selectedTransaction.createdAt) }}</p>
                <p><strong>User:</strong> {{ selectedTransaction.userUuid }}</p>
                <p><strong>Shortcode:</strong> {{ selectedTransaction.shortcode }}</p>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12">
                <h6>Metadata</h6>
                <pre class="bg-dark text-light p-3 rounded">{{ formatMetadata(selectedTransaction.metadata) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import api from "@/shared/menuApi";
import Datepicker from "@/components/vue3-date-time-picker.js";
import "@/components/vue3-date-time-picker.css";
import $ from 'jquery';

export default {
  name: "MerchantTransactions",
  components: {
    Datepicker
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      merchantUuid: null,
      dateRange: [],
      filters: {
        status: "",
        transactionType: ""
      },
      transactions: [],
      selectedTransaction: null,
      isLoading: false,
      transactionTypes: []
    };
  },
  methods: {
    async getTransactionTypes() {
      try {
        const response = await api.doGet('/api/merchant/transaction-types');
        if (response.status === "00") {
          this.transactionTypes = response.data;
        } else {
          this.toast.error(response.message || "Failed to fetch transaction types");
        }
      } catch (error) {
        console.error("Error fetching transaction types:", error);
        this.toast.error("An error occurred while fetching transaction types");
      }
    },
    async searchTransactions() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.toast.error("Please select a date range");
        return;
      }

      this.isLoading = true;
      try {
        const payload = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1],
          userUuid: this.merchantUuid,
          status: this.filters.status,
          transactionType: this.filters.transactionType
        };

        const response = await api.doPost('/api/merchant/transactions', payload);
        
        if (response.status === "00") {
          this.transactions = response.data;
          this.$nextTick(() => {
            this.renderDataTable();
          });
        } else {
          this.toast.error(response.message || "Failed to fetch transactions");
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
        this.toast.error("An error occurred while fetching transactions");
      } finally {
        this.isLoading = false;
      }
    },
    renderDataTable() {
      let table = $("#transactions-table").DataTable({
        destroy: true,
        paging: true,
        dom: "Bfrtip",
        buttons: ["copy", "csv", "excel"],
        data: this.transactions,
        columns: [
          { data: "transactionId" },
          { data: "transactionType" },
          { 
            data: "amount",
            render: (data) => this.formatAmount(data)
          },
          { 
            data: "status",
            render: (data) => `<span class="${this.getStatusBadgeClass(data)}">${data}</span>`
          },
          { 
            data: "createdAt",
            render: (data) => this.formatDate(data)
          },
          { 
            data: "transactionId",
            render: (data) => `<button class="btn btn-sm btn-outline-info" @click="viewTransactionDetails(${data})">
              <i class="fa fa-eye"></i>
            </button>`
          }
        ],
        responsive: true,
        order: [[4, "desc"]]
      });
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    formatAmount(currency, amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    },
    formatMetadata(metadata) {
      return JSON.stringify(metadata, null, 2);
    },
    getStatusBadgeClass(status) {
      const classes = {
        'SUCCESS': 'badge badge-success',
        'FAILED': 'badge badge-danger',
        'PENDING': 'badge badge-warning'
      };
      return classes[status] || 'badge badge-info';
    },
    closeModal() {
      jQuery("#transactionDetailsModal").modal("hide");
    },
    viewTransactionDetails(transaction) {
      this.selectedTransaction = transaction;
      jQuery("#transactionDetailsModal").modal("show");
    }
  },
  mounted() {
    // Set default date range to last 2 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 2);
    this.dateRange = [start, end];
    
    // Get merchantUuid from route params or localStorage
    this.merchantUuid = this.$route.params.merchantUuid || localStorage.getItem('merchantUuid');
    
    if (!this.merchantUuid) {
      this.toast.error("No merchant account found");
      return;
    }

    // First fetch transaction types
    this.getTransactionTypes().then(() => {
      // Then wait 10 seconds before fetching transactions
      setTimeout(() => {
        this.searchTransactions();
      },3000);
    });
  },
  beforeDestroy() {
    $('.select2').select2('destroy');
  }
};
</script>

<style scoped>
.card {
  background: #2b2e4c;
  border-radius: 10px;
  padding: 20px;
}

.table {
  color: #fff;
}

.table thead th {
  border-bottom: 1px solid #424351;
  color: #fff;
}

.table td {
  border-top: 1px solid #424351;
}

.badge {
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  color: #fff;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

.badge-danger {
  background-color: #dc3545;
  color: #fff;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

.modal-content {
  background-color: #2b2e4c;
  color: #fff;
}

.modal-header {
  border-bottom: 1px solid #424351;
}

.modal-footer {
  border-top: 1px solid #424351;
}

.close {
  color: #fff;
  text-shadow: none;
}

.close:hover {
  color: #fff;
  opacity: 0.75;
}

pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* DataTables Custom Styling */
.dataTables_wrapper .dataTables_filter input {
  background-color: #2b2e4c;
  color: #fff;
  border: 1px solid #424351;
  border-radius: 4px;
  padding: 5px 10px;
}

.dataTables_wrapper .dataTables_filter input:focus {
  border-color: #007bff;
  outline: none;
}

.dataTables_wrapper .dataTables_length select {
  background-color: #2b2e4c;
  color: #fff;
  border: 1px solid #424351;
  border-radius: 4px;
  padding: 5px 10px;
}

.dataTables_wrapper .dataTables_paginate .paginate_button {
  color: #fff !important;
  background: #2b2e4c !important;
  border: 1px solid #424351 !important;
}

.dataTables_wrapper .dataTables_paginate .paginate_button:hover {
  background: #424351 !important;
  border-color: #007bff !important;
}

.dataTables_wrapper .dataTables_paginate .paginate_button.current {
  background: #007bff !important;
  border-color: #007bff !important;
}

.dt-buttons .btn {
  margin-right: 5px;
}

.dt-buttons .btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.dt-buttons .btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
}
</style> 